import { useState } from "react";
import {
  Button,
  CostBreakdown,
  DeploymentTimeline,
  EnvVarEditor,
  LogViewer,
  MetricCard,
  QuotaMeter,
  RegionSelector,
  ResourceTable,
  StatusBadge,
  StatusDot,
  type CloudRegion,
  type CloudStatus,
  type DeploymentStepItem,
  type EnvVariable,
  type LogEntry,
  type ResourceColumn,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

interface SampleResource {
  id: string;
  name: string;
  type: string;
  region: string;
  status: CloudStatus;
  cpuUsage: number;
  cost: number;
}

const SAMPLE_RESOURCES: SampleResource[] = [
  {
    id: "inst-4f8a1e",
    name: "prod-api-eu-west-1a",
    type: "c6i.2xlarge",
    region: "eu-west-3 (Paris)",
    status: "running",
    cpuUsage: 42,
    cost: 184.5,
  },
  {
    id: "inst-9b2c3d",
    name: "worker-queue-consumer-01",
    type: "t4g.large",
    region: "eu-central-1 (Frankfurt)",
    status: "updating",
    cpuUsage: 78,
    cost: 48.2,
  },
  {
    id: "inst-1a7f8e",
    name: "staging-db-replica",
    type: "db.m6g.xlarge",
    region: "us-east-1 (N. Virginia)",
    status: "provisioning",
    cpuUsage: 15,
    cost: 112.0,
  },
  {
    id: "inst-3c4d5e",
    name: "analytics-batch-job",
    type: "c5n.4xlarge",
    region: "eu-west-3 (Paris)",
    status: "failed",
    cpuUsage: 0,
    cost: 0.0,
  },
];

const SAMPLE_LOGS: LogEntry[] = [
  {
    id: "log-1",
    timestamp: "11:42:01.004",
    level: "info",
    message: "HTTP GET /v1/cloud/resources 200 OK - 12ms",
  },
  {
    id: "log-2",
    timestamp: "11:42:03.120",
    level: "warn",
    message: "High memory utilization threshold reached on instance pool: 84%",
    meta: { threshold: 80, current: 84, node: "node-worker-04" },
  },
  {
    id: "log-3",
    timestamp: "11:42:05.891",
    level: "error",
    message: "Connection timeout while acquiring pooled PostgreSQL client",
    meta: { poolSize: 50, activeConnections: 50, timeoutMs: 5000 },
  },
  {
    id: "log-4",
    timestamp: "11:42:08.450",
    level: "info",
    message: "Auto-scaling trigger invoked: launching 2 additional compute replicas",
  },
];

const SAMPLE_ENV_VARS: EnvVariable[] = [
  {
    id: "env-1",
    key: "DATABASE_URL",
    value: "postgresql://postgres:secret_pass@db.internal:5432/prod",
    isSecret: true,
    environments: ["production"],
  },
  {
    id: "env-2",
    key: "NEXT_PUBLIC_API_ENDPOINT",
    value: "https://api.nexty.cloud/v1",
    isSecret: false,
    environments: ["production", "preview", "development"],
  },
  {
    id: "env-3",
    key: "REDIS_CACHE_SECRET",
    value: "red_sec_993849182304918230912",
    isSecret: true,
    environments: ["production", "preview"],
  },
];

const SAMPLE_PIPELINE_STEPS: DeploymentStepItem[] = [
  { id: "step-1", name: "Checkout & Git Submodules", status: "succeeded", duration: "1.2s" },
  { id: "step-2", name: "Build Container Image (Docker)", status: "succeeded", duration: "24.5s" },
  { id: "step-3", name: "Execute Database Migrations", status: "succeeded", duration: "3.8s" },
  { id: "step-4", name: "Canary Traffic Rollout (10% -> 100%)", status: "updating", duration: "En cours..." },
];

export function CloudSection() {
  const [selectedRegion, setSelectedRegion] = useState<CloudRegion | undefined>();
  const [envVars, setEnvVars] = useState<EnvVariable[]>(SAMPLE_ENV_VARS);

  const columns: ResourceColumn<SampleResource>[] = [
    {
      key: "name",
      header: "Nom de l'instance",
      priority: "primary",
      cell: (row) => <span className="font-mono text-xs font-semibold">{row.name}</span>,
    },
    {
      key: "status",
      header: "Statut",
      priority: "secondary",
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "type",
      header: "Type",
      priority: "secondary",
      cell: (row) => <span className="font-mono text-xs text-fg-muted">{row.type}</span>,
    },
    {
      key: "region",
      header: "Région",
      priority: "detail",
      cell: (row) => <span className="text-xs text-fg-muted">{row.region}</span>,
    },
    {
      key: "cost",
      header: "Coût / mois",
      align: "end",
      priority: "secondary",
      cell: (row) => <span className="font-mono font-medium">{row.cost} €</span>,
    },
  ];

  return (
    <Section
      id="cloud-console"
      eyebrow="Console Cloud"
      title="Composants d'infrastructure & FinOps"
      description="Une suite complète de composants conçus pour les consoles cloud d'entreprise, les métriques en temps réel, le streaming de logs et la gestion d'infrastructures."
    >
      {/* Statuts Cloud */}
      <SubSection
        title="Badges & Pastilles d'état Cloud"
        hint="Vocabulaire fermé avec animation transitoire pour les états en mouvement"
      >
        <Preview
          layout="stack"
          label="status.tsx"
          code={`<StatusBadge status="running" />
<StatusBadge status="provisioning" />
<StatusBadge status="degraded" />
<StatusBadge status="failed" />
<StatusBadge status="stopped" />`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status="running" />
            <StatusBadge status="succeeded" />
            <StatusBadge status="provisioning" />
            <StatusBadge status="updating" />
            <StatusBadge status="degraded" />
            <StatusBadge status="failed" />
            <StatusBadge status="stopped" />
            <StatusBadge status="unknown" />
          </div>

          <div className="flex items-center gap-4 pt-2">
            <span className="text-xs text-fg-muted inline-flex items-center gap-1.5">
              <StatusDot status="running" /> En ligne
            </span>
            <span className="text-xs text-fg-muted inline-flex items-center gap-1.5">
              <StatusDot status="provisioning" /> En cours de création (pulse)
            </span>
            <span className="text-xs text-fg-muted inline-flex items-center gap-1.5">
              <StatusDot status="failed" /> Hors service
            </span>
          </div>
        </Preview>
      </SubSection>

      {/* Cartes de métriques */}
      <SubSection
        title="Cartes de métriques & Sparklines SVG"
        hint="vector-effect: non-scaling-stroke pour garder une épaisseur constante"
      >
        <Preview
          layout="row"
          label="metric-card.tsx"
          code={`<MetricCard
  label="Latence P99"
  value="42"
  unit="ms"
  delta={-14}
  deltaGoodWhen="down"
  series={[65, 54, 48, 52, 44, 42]}
  period="24 dernières heures"
/>`}
        >
          <MetricCard
            label="Requêtes / sec"
            value="14 250"
            unit="req/s"
            delta={18.4}
            deltaGoodWhen="up"
            series={[8000, 9200, 11000, 10500, 12800, 14250]}
            period="Dernières 24 heures"
          />

          <MetricCard
            label="Latence P99"
            value="42"
            unit="ms"
            delta={-12.5}
            deltaGoodWhen="down"
            series={[68, 59, 52, 48, 45, 42]}
            period="Dernières 24 heures"
          />

          <MetricCard
            label="Taux d'erreur HTTP 5xx"
            value="1.42"
            unit="%"
            delta={35.0}
            deltaGoodWhen="down"
            series={[0.2, 0.4, 0.5, 0.8, 1.1, 1.42]}
            period="Alerte active"
          />
        </Preview>
      </SubSection>

      {/* Jauges de quotas */}
      <SubSection
        title="Jauges de Quotas & Limites"
        hint="Seuils d'avertissement (75%) et critique (90%) avec calcul dynamique du restant"
      >
        <Preview
          layout="stack"
          label="quota-meter.tsx"
          code={`<QuotaMeter label="RAM Allouée" used={68} limit={128} unit="Gio" />
<QuotaMeter label="Egress Réseau" used={940} limit={1000} unit="Gio" warnAt={75} dangerAt={90} />`}
        >
          <div className="flex flex-col gap-5 max-w-xl">
            <QuotaMeter label="Instances vCPU" used={24} limit={64} unit="vCPU" />
            <QuotaMeter label="Stockage SSD NVMe" used={780} limit={1000} unit="Gio" warnAt={70} />
            <QuotaMeter label="Bande passante mensuelle" used={960} limit={1000} unit="Gio" dangerAt={90} />
          </div>
        </Preview>
      </SubSection>

      {/* Console de logs */}
      <SubSection
        title="Flux de Logs en Temps Réel"
        hint="Recherche, filtrage par niveau (ERR/WARN/INFO), live tailing & métadonnées JSON"
      >
        <LogViewer logs={SAMPLE_LOGS} title="api-server-production / main" />
      </SubSection>

      {/* Tableau d'inventaire */}
      <SubSection
        title="Inventaire de Ressources (Tableau Adaptive)"
        hint="Mode tableau sur écran large, fiches tactiles sur mobile"
      >
        <ResourceTable
          rows={SAMPLE_RESOURCES}
          columns={columns}
          rowId={(r) => r.id}
          rowStatus={(r) => r.status}
          selectable
          rowActions={() => <Button size="xs" variant="ghost">Gérer</Button>}
        />
      </SubSection>

      {/* Variables d'environnement */}
      <SubSection
        title="Variables d'Environnement & Secrets"
        hint="Masquage automatique des secrets, copie immédiate et portée par environnement"
      >
        <EnvVarEditor variables={envVars} onChange={setEnvVars} />
      </SubSection>

      {/* Sélecteur de région */}
      <SubSection
        title="Sélecteur de Régions Cloud & Latence"
        hint="Ping réseau en direct et badges de conformité réglementaire"
      >
        <RegionSelector
          selectedRegionId={selectedRegion?.id}
          onSelect={setSelectedRegion}
        />
      </SubSection>

      {/* Pipeline de Déploiement */}
      <SubSection
        title="Pipeline de Déploiement (CI/CD Timeline)"
        hint="Visualisation d'avancement par étape et intégration Git"
      >
        <DeploymentTimeline
          title="Déploiement #v2.4.1"
          branchName="main"
          commitHash="a8f9c2d1"
          commitMessage="feat(cloud): add LogViewer and EnvVarEditor components"
          authorName="Arthur"
          steps={SAMPLE_PIPELINE_STEPS}
        />
      </SubSection>

      {/* FinOps & Coûts */}
      <SubSection
        title="FinOps & Décomposition des Coûts"
        hint="Projection budgétaire mensuelle et répartition par type de service"
      >
        <CostBreakdown
          currentTotal={842.5}
          projectedTotal={1120.0}
          budgetLimit={1000.0}
          categories={[
            { id: "c1", name: "Instances Compute (EC2/EKS)", amount: 450, colorClass: "bg-accent" },
            { id: "c2", name: "Bases de données (RDS PostgreSQL)", amount: 240, colorClass: "bg-info-fg" },
            { id: "c3", name: "Stockage S3 & Backups", amount: 92.5, colorClass: "bg-warning-fg" },
            { id: "c4", name: "Bande passante Egress", amount: 60, colorClass: "bg-success-fg" },
          ]}
        />
      </SubSection>
    </Section>
  );
}
